import { createMenuConfig, normalizeMenuRoutePath } from "helpers/appSidebarMenu";

export const APP_TITLE = "Ancile Services";

const ROUTE_TITLE_OVERRIDES = {
  login: "Login",
  logout: "Logout",
  "forgot-password": "Forgot Password",
  register: "Register",
  profile: "Profile",
  "pages-404": "404 Error",
  "pages-500": "500 Error",
  dashboard: "Dashboard",
  "backend-dashboard": "Backend Dashboard",
  "recruiter-dashboard": "Recruiter Dashboard",
  "account-executive-dashboard": "Account Executive Dashboard",
  "client-details": "Client Details",
  "call-details": "Call Details",
  "call/details": "Call Details",
  "find-company-user": "Find Company User",
  "client-review-add": "Client Review",
  "client-review-edit": "Client Review",
  "Home/Index": "Client Review",
  "job-description-upsert": "Job Description",
  "client-register-add-edit": "Client Register",
  "add-edit-onboarding": "Onboarding",
  "add-edit-mail-template": "Mail Template",
  "manage-user-add-edit": "Manage User",
  "user-type-add-edit": "User Type",
  "company-user": "Company User",
  "company-user-add-edit": "Company User",
  "edit-call-audit": "Edit Call Audit",
  "view-call-audit": "View Call Audit",
  "monthly-details": "Monthly Details",
  "monthly-details-two": "Monthly Details",
  "monthly-details-three": "Monthly Details",
  "team-recognition-monthly-details": "Monthly Details",
  "no-permission": "No Permission",
  "access-denied": "Access Denied",
  "all-notifications": "Notifications",
  "simple-all-notifications": "Notifications",
  "home-health": "API Status",
  "client-journey": "Client Journey",
  "today/vm": "Today",
  "order-in-progress": "Order In Progress",
  "in-progress": "In Progress",
  "ready-to-send-update": "Ready To Send Update",
  "further-review": "Further Review",
  "fedex-review": "FedEx Review",
  "upload-bulk-cv": "Upload Bulk CV",
  "broadcast-notification": "Broadcast Notification",
  "compliancy-report": "Compliance Report",
  "not-interested-anymore": "Not Interested Report",
  "User/List": "Manage User",
  "client-job-description": "Job Description",
  "client-appreciations": "Client Appreciation",
};

function flattenMenuRouteTitles(menuConfig) {
  const map = new Map();

  const walkItems = (items) => {
    for (const item of items || []) {
      if (item.children?.length) {
        walkItems(item.children);
      }
      if (item.routeLink && item.name) {
        const key = normalizeMenuRoutePath(item.routeLink);
        if (key) {
          map.set(key, item.name);
        }
      }
    }
  };

  for (const group of menuConfig || []) {
    walkItems(group.items);
  }

  return map;
}

const MENU_ROUTE_TITLES = flattenMenuRouteTitles(createMenuConfig({}));

const formatPathSegment = (segment = "") =>
  String(segment)
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const looksLikeRouteParam = (value = "") =>
  /^\d+$/.test(value) || /^[a-f0-9-]{20,}$/i.test(value);

function resolveTitleFromMap(normalized, titleMap) {
  if (titleMap.has(normalized)) {
    return titleMap.get(normalized);
  }

  let matchedTitle = null;
  let matchedLen = 0;

  for (const [key, title] of titleMap.entries()) {
    if (normalized === key || normalized.startsWith(`${key}/`)) {
      if (key.length > matchedLen) {
        matchedLen = key.length;
        matchedTitle = title;
      }
    }
  }

  return matchedTitle;
}

function resolveTitleFromOverrides(normalized) {
  if (ROUTE_TITLE_OVERRIDES[normalized]) {
    return ROUTE_TITLE_OVERRIDES[normalized];
  }

  let matchedTitle = null;
  let matchedLen = 0;

  for (const [key, title] of Object.entries(ROUTE_TITLE_OVERRIDES)) {
    if (normalized === key || normalized.startsWith(`${key}/`)) {
      if (key.length > matchedLen) {
        matchedLen = key.length;
        matchedTitle = title;
      }
    }
  }

  return matchedTitle;
}

export function getPageTitle(pathname = "") {
  const normalized = normalizeMenuRoutePath(pathname);
  if (!normalized) {
    return APP_TITLE;
  }

  const overrideTitle = resolveTitleFromOverrides(normalized);
  if (overrideTitle) {
    return overrideTitle;
  }

  const menuTitle = resolveTitleFromMap(normalized, MENU_ROUTE_TITLES);
  if (menuTitle) {
    return menuTitle;
  }

  const segments = normalized.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1];

  if (lastSegment && looksLikeRouteParam(lastSegment) && segments.length > 1) {
    const parentPath = segments.slice(0, -1).join("/");
    const parentTitle =
      resolveTitleFromOverrides(parentPath) ||
      resolveTitleFromMap(parentPath, MENU_ROUTE_TITLES);
    if (parentTitle) {
      return parentTitle;
    }
  }

  return formatPathSegment(lastSegment || normalized);
}

export function formatDocumentTitle(pageTitle) {
  if (!pageTitle || pageTitle === APP_TITLE) {
    return APP_TITLE;
  }
  return `${pageTitle} | ${APP_TITLE}`;
}

export function setDocumentTitle(pathname = "") {
  document.title = formatDocumentTitle(getPageTitle(pathname));
}
